import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Clock, TrendingDown, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const ICONS = [Clock, TrendingDown, AlertTriangle];

function AnimatedCard({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

export default function ProblemSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const cards = t('problem.cards');

  return (
    <section id="problem" className="relative pt-20 pb-10 sm:pt-28 sm:pb-14 px-6 sm:px-12 lg:px-24" data-testid="problem-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium max-w-3xl leading-snug">
            {t('problem.heading')}{' '}
            <span className="text-[#BDD6B5]">{t('problem.headingGreen')}</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/78 max-w-3xl leading-relaxed">
            {t('problem.body')}
          </p>
          <p className="mt-4 text-[#BDD6B5]/80 font-medium text-sm">
            {t('problem.note')}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.isArray(cards) && cards.map((card, i) => {
            const Icon = ICONS[i];
            return (
              <AnimatedCard key={i} delay={i * 0.15}>
                <div className="glass-card p-8 h-full" data-testid={`problem-card-${i}`}>
                  <Icon className="text-[#BDD6B5] mb-4" size={28} strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold text-white mb-3">{card.title}</h3>
                  <p className="text-sm text-white/78 leading-relaxed">{card.desc}</p>
                </div>
              </AnimatedCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
